// ============================================================================
// SnLinkDevice — SN-Link V3 HID Flash Programmer (stub)
// ============================================================================
//
// All flash operations are stubbed. Real implementation requires:
// 1. Confirmed VID/PID from physical SN-Link V3 hardware
// 2. Wireshark capture verification of the protocol
// 3. Testing with actual Sonix target MCUs

import type {
  HIDFlashProgrammer,
  ProgrammerInfo,
  ChipDefinition,
  DeviceIdResult,
  ConfigBytes,
  FlashProgress8051,
  ProgrammerEvent,
} from '@/types/hid8051';
import { HidTransport } from '../HidTransport';
import { SNLINK_HID_FILTERS, SNLINK_REPORT_SIZE, SnLinkCmd } from './constants';
import { buildPacket, parseResponse } from './protocol';

const STUB_ERROR = 'SN-Link protocol stub: requires hardware verification and Wireshark captures';

type EventHandler = (...args: unknown[]) => void;

export class SnLinkDevice implements HIDFlashProgrammer {
  readonly programmerFamily = 'snlink' as const;
  private transport: HidTransport;
  private _connected = false;
  private eventHandlers: Map<ProgrammerEvent, EventHandler[]> = new Map();

  private constructor(transport: HidTransport) {
    this.transport = transport;
  }

  get connected(): boolean {
    return this._connected;
  }

  /**
   * Request an SN-Link device via WebHID and create an instance.
   */
  static async requestDevice(): Promise<SnLinkDevice> {
    const devices = await navigator.hid.requestDevice({
      filters: SNLINK_HID_FILTERS,
    });

    if (devices.length === 0) {
      throw new Error('No SN-Link programmer selected');
    }

    const device = devices[0];
    const transport = new HidTransport(device, SNLINK_REPORT_SIZE);
    await transport.open();

    return new SnLinkDevice(transport);
  }

  async connect(): Promise<ProgrammerInfo> {
    // GET_FW_VER to verify communication
    const packet = buildPacket(SnLinkCmd.GET_FW_VER);
    const response = await this.transport.sendAndReceive(packet);
    const parsed = parseResponse(response, SnLinkCmd.GET_FW_VER);

    this._connected = true;

    const info: ProgrammerInfo = {
      name: this.transport.productName || 'SN-Link V3',
      firmwareVersion: parsed.param1,
      vid: this.transport.vendorId,
      pid: this.transport.productId,
      protocolVersion: 1,
    };

    this.emit('connected');
    return info;
  }

  async disconnect(): Promise<void> {
    if (this._connected) {
      try {
        const packet = buildPacket(SnLinkCmd.DISCONNECT);
        await this.transport.sendReport(packet);
      } catch {
        // Best-effort disconnect
      }
    }
    await this.transport.close();
    this._connected = false;
    this.emit('disconnected');
  }

  async setConfig(
    _chip: ChipDefinition,
    _clockKHz?: number,
    _vccMv?: number
  ): Promise<void> {
    throw new Error(STUB_ERROR);
  }

  async readDeviceId(): Promise<DeviceIdResult> {
    throw new Error(STUB_ERROR);
  }

  async readConfig(): Promise<ConfigBytes> {
    throw new Error(STUB_ERROR);
  }

  async massErase(): Promise<void> {
    throw new Error(STUB_ERROR);
  }

  async eraseRange(_address: number, _size: number): Promise<void> {
    throw new Error(STUB_ERROR);
  }

  async writeFlash(
    _address: number,
    _data: Uint8Array,
    _onProgress?: (p: FlashProgress8051) => void
  ): Promise<void> {
    throw new Error(STUB_ERROR);
  }

  async readFlash(
    _address: number,
    _length: number,
    _onProgress?: (p: FlashProgress8051) => void
  ): Promise<Uint8Array> {
    throw new Error(STUB_ERROR);
  }

  async verifyFlash(
    _address: number,
    _data: Uint8Array,
    _onProgress?: (p: FlashProgress8051) => void
  ): Promise<boolean> {
    throw new Error(STUB_ERROR);
  }

  async resetToAprom(): Promise<void> {
    throw new Error(STUB_ERROR);
  }

  async resetToLdrom(): Promise<void> {
    throw new Error(STUB_ERROR);
  }

  on(event: ProgrammerEvent, handler: EventHandler): void {
    const handlers = this.eventHandlers.get(event) ?? [];
    handlers.push(handler);
    this.eventHandlers.set(event, handlers);
  }

  private emit(event: ProgrammerEvent, ...args: unknown[]): void {
    const handlers = this.eventHandlers.get(event) ?? [];
    for (const handler of handlers) {
      handler(...args);
    }
  }
}
